import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { ReportRowType } from "../hooks/useReportRows";
import { ReportRow } from "../api/models/report.models";
import { useSearchParams } from "react-router";

type ReportFormContextProps = {
  combinedWeight: number | null;
  setCombinedWeight: Dispatch<SetStateAction<number | null>>;
  reportType: ReportRowType;
  setReportType: Dispatch<SetStateAction<ReportRowType>>;
  ecoWeightLimitReached: boolean;
  setEcoWeightLimitReached: Dispatch<SetStateAction<boolean>>;
  displayEcomodError: boolean;
  setDisplayEcomodError: Dispatch<SetStateAction<boolean>>;
  isUpdatingReportRow: boolean;
  setIsUpdatingReportRow: Dispatch<SetStateAction<boolean>>;
  showCreateProductView: boolean;
  setShowCreateProductView: Dispatch<SetStateAction<boolean>>;
  showEditReportRowView: ReportRow | null;
  setShowEditReportRowView: Dispatch<SetStateAction<ReportRow | null>>;
  showCreateMaterialView: boolean;
  setShowCreateMaterialView: Dispatch<SetStateAction<boolean>>;
};

export const ReportFormContext = createContext<ReportFormContextProps>({
  combinedWeight: null,
  setCombinedWeight: () => {},
  reportType: ReportRowType.SIMPLE,
  setReportType: () => {},
  ecoWeightLimitReached: false,
  setEcoWeightLimitReached: () => {},
  displayEcomodError: false,
  setDisplayEcomodError: () => {},
  isUpdatingReportRow: false,
  setIsUpdatingReportRow: () => {},
  showCreateProductView: false,
  setShowCreateProductView: () => {},
  showEditReportRowView: null,
  setShowEditReportRowView: () => {},
  showCreateMaterialView: false,
  setShowCreateMaterialView: () => {},
} as ReportFormContextProps);

export const ReportFormProvider = ({ children }) => {
  const [combinedWeight, setCombinedWeight] = useState<number | null>(null);
  const [reportType, setReportType] = useState<ReportRowType>(
    ReportRowType.SIMPLE
  );
  const [showCreateProductView, setShowCreateProductView] = useState(false);
  const [ecoWeightLimitReached, setEcoWeightLimitReached] = useState(false);
  const [isUpdatingReportRow, setIsUpdatingReportRow] = useState(false);
  const [showCreateMaterialView, setShowCreateMaterialView] = useState(false);

  const [showEditReportRowView, setShowEditReportRowView] =
    useState<ReportRow | null>(null);

  const [displayEcomodError, setDisplayEcomodError] = useState(false);

  // if any of the rows is simple, and the report type is ECOMOD, show error
  useEffect(() => {
    if (ecoWeightLimitReached && reportType !== ReportRowType.ECOMOD) {
      setDisplayEcomodError(true);
    } else {
      setDisplayEcomodError(false);
    }
  }, [ecoWeightLimitReached]);

  const value: ReportFormContextProps = {
    combinedWeight,
    setCombinedWeight,
    reportType,
    setReportType,
    ecoWeightLimitReached,
    setEcoWeightLimitReached,
    displayEcomodError,
    setDisplayEcomodError,
    isUpdatingReportRow,
    setIsUpdatingReportRow,
    showCreateProductView,
    setShowCreateProductView,
    showEditReportRowView,
    setShowEditReportRowView,
    showCreateMaterialView,
    setShowCreateMaterialView,
  };

  return (
    <ReportFormContext.Provider value={value}>
      {children}
    </ReportFormContext.Provider>
  );
};

export const useReportFormContext = () => useContext(ReportFormContext);
